import moment from 'moment';
import { useEffect, useState } from 'react';

import { getActiveUsers, getAuthMethods, getCountries } from '../api';
import { Filter } from '../api/analytics/type';
import { useConfig } from '../contexts/config';
import { AnalyticsFilter, Graphic, Period, UseAnalytics } from './types';

export const ALL_PROJECTS_COUNTRIES = '0';

const serializePeriod = (period: Period) => ({
  from: period.from.toISOString().split('T')[0],
  to: period.to.toISOString().split('T')[0]
});

export const useAnalyticsData = (filterStart: AnalyticsFilter): UseAnalytics => {
  const { merchantId } = useConfig();

  const [usersData, setUsersData] = useState<Graphic | null>(null);
  const [authMethodsData, setAuthMethodsData] = useState<Graphic | null>(null);
  const [authCountriesData, setAuthCountriesData] = useState<Graphic | null>(null);

  const [errorUsersData, setErrorUsersData] = useState<boolean>(false);
  const [errorAuthMethodsData, setErrorAuthMethodsData] = useState<boolean>(false);
  const [errorAuthCountriesData, setErrorAuthCountriesData] = useState<boolean>(false);

  const serializeFilter = (filter: AnalyticsFilter) => {
    const period = serializePeriod(filter.datePeriod);
    const userFilter: Filter = {
      start_date: period.from,
      end_date: period.to,
      filters: []
    };

    if (filter.loginProject && filter.loginProject !== ALL_PROJECTS_COUNTRIES) {
      userFilter.filters.push({
        field: 'project_id',
        value: filter.loginProject
      });
    }

    return userFilter;
  };

  const updateUserData = async (filter: Filter) => {
    try {
      const response = await getActiveUsers(merchantId, { filter });
      let points = response.points || [];
      points = points.filter((p) => moment(p.key, moment.ISO_8601).isValid());
      points.sort((a, b) => moment(a.key).diff(moment(b.key)));
      setUsersData({
        ...response,
        points
      });
    } catch (err) {
      setErrorUsersData(true);
    }
  };

  const updateAuthMethodsData = async (filter: Filter) => {
    try {
      const response = await getAuthMethods(merchantId, { filter });
      let points = response.points || [];
      points = points.filter((p) => typeof p.key === 'string' && typeof p.value === 'number');
      const otherId = points.findIndex((p) => p.key === 'other');
      const withoutOthers = points.filter((p) => p.key !== 'other');
      withoutOthers.sort((p1, p2) => p2.value - p1.value);
      const showOther = otherId !== -1 && points[otherId].value !== 0;
      setAuthMethodsData({
        ...response,
        points: showOther ? [...withoutOthers, points[otherId]] : withoutOthers
      });
    } catch (err) {
      setErrorAuthMethodsData(true);
    }
  };

  const updateCountryData = async (filter: Filter) => {
    try {
      const response = await getCountries(merchantId, { filter });
      let points = response.points || [];
      points = points.filter((p) => typeof p.key === 'string' && typeof p.value === 'number');
      points.sort((p1, p2) => p2.value - p1.value);
      setAuthCountriesData({
        ...response,
        points
      });
    } catch (err) {
      setErrorAuthCountriesData(true);
    }
  };

  const updateData = (filter: AnalyticsFilter) => {
    const userFilter = serializeFilter(filter);
    updateUserData(userFilter);
    updateAuthMethodsData(userFilter);
    updateCountryData(userFilter);
  };

  useEffect(() => {
    updateData(filterStart);
  }, []);

  return {
    errorUsersData,
    errorAuthMethodsData,
    errorAuthCountriesData,
    users: usersData,
    authMethods: authMethodsData,
    authCountries: authCountriesData,
    updateAnalytics: updateData
  };
};
